import { NotFound } from 'fejl'
import { computed, observable, action, runInAction } from 'mobx'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type SessionStore } from '@/features/iam/auth/SessionStore'
import { type WorkspaceMembershipStore } from '@/features/iam/workspaces/WorkspaceMembershipStore'
import { type WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { type LastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'
import type { Storage } from '@/infra/storage'
import { ModalViewModel } from '@/infra/ModalViewModel'

export class WorkspaceSelectorViewModel {
  @observable
  accessor searchText = ''

  @observable
  accessor workspace: Workspace | null = null

  @observable
  accessor isDefaultToAllWorkspaces = false

  /**
   * The workspace selector panel.
   */
  readonly selector = new ModalViewModel()

  constructor(
    private readonly _workspaceMembershipsStore: WorkspaceMembershipStore,
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _sessionStore: SessionStore,
    private readonly _checkMemberExists: (
      workspace: Workspace,
    ) => Promise<boolean>,
    private readonly _lastWorkspaceStorage: LastWorkspaceStorage,
    private readonly _localStorage: Storage,
    private readonly _sessionStorage: Storage,
  ) {
    runInAction(() => {
      const stored = this._localStorage.getItem(
        'WorkspaceSelector.DefaultToAllWorkspaces',
      )
      this.isDefaultToAllWorkspaces = stored === 'true'
    })
  }

  /**
   * The current user's workspace memberships.
   */
  @computed
  get memberships() {
    return this._workspaceMembershipsStore.memberships.slice()
  }

  /**
   * The current user's workspace memberships, sorted by name.
   */
  @computed
  get sortedMemberships() {
    return this.memberships
      .slice()
      .sort((a, b) => a.workspace.name.localeCompare(b.workspace.name))
  }

  /**
   * User's workspaces that have been filtered.
   */
  @computed
  get filteredMemberships() {
    return this.sortedMemberships.filter(
      (x) =>
        x.workspace.name
          .toLowerCase()
          .includes(this.searchText.toLowerCase()) ||
        x.workspace.slug.toLowerCase().includes(this.searchText.toLowerCase()),
    )
  }

  /**
   * Whether the user may default lists to all of their workspaces.
   */
  @computed
  get showDefaultToAllWorkspaces() {
    return this.memberships.some((m) => m.member.permissions.adminJobView)
  }

  /**
   * Whether the workspace is the one selected in the session.
   */
  isSelected(workspace: Workspace) {
    return (
      this._sessionStore.hasWorkspaceSelected &&
      this._sessionStore.currentWorkspace.id === workspace.id
    )
  }

  @action.bound
  setSearchText(text?: string) {
    this.searchText = text || ''
  }

  @action.bound
  toggleSelector() {
    if (this.selector.showing) {
      this.selector.close()
    } else {
      this.selector.show()
    }
  }

  @action.bound
  toggleDefaultToAllWorkspaces() {
    this.isDefaultToAllWorkspaces = !this.isDefaultToAllWorkspaces
    this._localStorage.setItem(
      'WorkspaceSelector.DefaultToAllWorkspaces',
      this.isDefaultToAllWorkspaces ? 'true' : 'false',
    )
  }

  /**
   * Selects the workspace once it and the member exist.
   */
  @action.bound
  async selectWorkspace(workspace: Workspace) {
    if (!workspace) {
      return
    }

    // Make sure we have the most up-to-date version of the workspace.
    const freshWorkspace = await this._workspaceStore.fetchWorkspace(
      workspace.id,
    )

    if (!freshWorkspace) {
      throw new Error('The workspace you selected does not exist.')
    }

    if (await this._checkMemberExists(freshWorkspace)) {
      // The workspace and member exists, so we can select it in the session store.
      // This will make the WorkspaceGateway render the actual app.
      await this._sessionStore.selectWorkspace(freshWorkspace)
      // Update the observable workspace property for other components to react to
      runInAction(() => {
        this.workspace = freshWorkspace
      })
    }
    this.selector.close()
  }

  /**
   * Selects the workspace the user picked.
   */
  @action.bound
  async select(workspace: Workspace) {
    // A failed selection leaves the selector open for another pick.
    await this.selectWorkspace(workspace).catch(() => undefined)
  }

  /**
   * Attempts to pre-select a workspace based on the user's last visit.
   */
  async preSelectWorkspace(inviteWorkspace?: Workspace) {
    if (!this._sessionStore.isAuthenticated) {
      return false
    }
    if (inviteWorkspace) {
      if (await this._checkMemberExists(inviteWorkspace)) {
        return this._sessionStore.selectWorkspace(inviteWorkspace)
      }
    }

    let lastSelectedWorkspace = await this._getPreselectedWorkspace()

    if (
      !lastSelectedWorkspace &&
      this._workspaceMembershipsStore.memberships.length > 0
    ) {
      lastSelectedWorkspace =
        this._workspaceMembershipsStore.memberships[0].workspace
    }

    if (lastSelectedWorkspace) {
      await this.selectWorkspace(lastSelectedWorkspace)
      this.selector.close()
    }
  }

  private _getLastSelectedWorkspace(userId: string) {
    return this._lastWorkspaceStorage.load(userId)
  }

  private async _getPreselectedWorkspace() {
    const nativeWorkspaceId = this._sessionStorage.getItem('lastWorkspaceId')
    const userId = this._sessionStore.currentUser.publicId
    const lastSelectedWorkspaceId =
      nativeWorkspaceId ||
      (userId ? this._getLastSelectedWorkspace(userId) : null)

    // Check if the selected workspace is still part of the user's memberships.
    // Might be a case where the user was removed from the workspace and their last selected workspace was the one they got kicked out of.
    const isLastSelectedWorkspaceValid =
      this._workspaceMembershipsStore.memberships.find(
        (x) => x.workspace.id === lastSelectedWorkspaceId,
      ) !== undefined

    if (!lastSelectedWorkspaceId || !isLastSelectedWorkspaceValid) {
      return null
    }

    const storedWorkspace = this._workspaceStore.getWorkspaceByIdOrSlug(
      lastSelectedWorkspaceId,
    )
    if (storedWorkspace) {
      return storedWorkspace
    }

    return this._workspaceStore
      .fetchWorkspaceByIdOrSlug(lastSelectedWorkspaceId)
      .catch(NotFound.ignore(null))
  }
}
